import {
  byServicePosition,
  clockAt,
  repeatDates,
  repeatProblem,
  repeatSummary,
  reorder as reorderList,
  runningTimes,
  templatePickerOrder,
  teamsForViewer,
  type PlanRepeat,
} from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi } from '../api-context.tsx'
import {
  servicesKeys,
  useTeams,
  type PlanEdit,
  type ServicePosition,
  type ServiceType,
} from './useServices.ts'

/**
 * Plan templates (docs/14-TEAMS-PEOPLE-TEMPLATES.md): a service type's usual
 * plans, edited like a plan — the same running-order screens, the same
 * rules on the server — and the New plan panel that makes plans from them,
 * one or several at once.
 */

/** The kinds of change a template editor makes (its mutations, by name). */
export type TemplateEdit =
  | Extract<PlanEdit, 'addItem' | 'updateItem' | 'removeItem' | 'reorder'>
  | Extract<PlanEdit, 'addTime' | 'updateTime' | 'removeTime'>
  | 'updateTemplate'
  | 'setNeed'

export const templateKeys = {
  all: () => [...servicesKeys.all(), 'templates'] as const,
  /** A service type's templates. */
  list: (serviceTypeId: string) => [...templateKeys.all(), 'list', serviceTypeId] as const,
  one: (id: string) => [...templateKeys.all(), 'one', id] as const,
  /** Every change made to one template, whichever panel made it (mutation keys). */
  edits: (id: string) => [...servicesKeys.all(), 'template-edits', id] as const,
  edit: (id: string, edit: TemplateEdit) =>
    [...servicesKeys.all(), 'template-edits', id, edit] as const,
}

export function useServiceTypeTemplates(
  serviceTypeId: string,
  options: { enabled?: boolean } = {},
) {
  const api = useApi()
  return useQuery({
    queryKey: templateKeys.list(serviceTypeId),
    enabled: serviceTypeId !== '' && options.enabled !== false,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services['service-types']({
        id: serviceTypeId,
      }).templates.get()
      if (error) throw error
      return data
    },
  })
}

export type TemplateSummary = NonNullable<
  ReturnType<typeof useServiceTypeTemplates>['data']
>[number]

export function useTemplate(templateId: string) {
  const api = useApi()
  return useQuery({
    queryKey: templateKeys.one(templateId),
    enabled: templateId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.templates({ id: templateId }).get()
      if (error) throw error
      return data
    },
  })
}

export type PlanTemplate = NonNullable<ReturnType<typeof useTemplate>['data']>
export type TemplateTime = PlanTemplate['times'][number]
export type TemplateTimeKind = 'service' | 'rehearsal' | 'other'

/** A new, empty template of a service type (its first becomes the default). */
export function useCreateTemplate(serviceTypeId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { name: string; notes?: string | null; isDefault?: boolean }) => {
      const { data, error } = await api.api.v1.services['service-types']({
        id: serviceTypeId,
      }).templates.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      if (data) queryClient.setQueryData(templateKeys.one(data.id), data)
      void queryClient.invalidateQueries({ queryKey: templateKeys.list(serviceTypeId) })
    },
  })
}

/** "Save as template" on a plan: its order, times, needed positions and notes. */
export function useSavePlanAsTemplate(planId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { name: string; isDefault?: boolean }) => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).template.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      if (!data) return
      queryClient.setQueryData(templateKeys.one(data.id), data)
      void queryClient.invalidateQueries({ queryKey: templateKeys.list(data.serviceTypeId) })
    },
  })
}

/**
 * One template's editor: its details, its running order (the plan's
 * shapes and rules — a reorder shows at once and rolls back if refused),
 * its times and the people each position needs. Every change files itself
 * under the template (`templateKeys.edits`), so one save status reads them all.
 */
export function useTemplateEditor(templateId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const key = templateKeys.one(templateId)
  const template = useTemplate(templateId)
  const route = () => api.api.v1.services.templates({ id: templateId })
  const put = (data: PlanTemplate | undefined) => {
    if (!data) return
    queryClient.setQueryData(key, data)
    // The list's counts and lengths follow.
    void queryClient.invalidateQueries({ queryKey: templateKeys.list(data.serviceTypeId) })
  }

  const updateTemplate = useMutation({
    mutationKey: templateKeys.edit(templateId, 'updateTemplate'),
    mutationFn: async (body: {
      name?: string
      notes?: string | null
      categoryNotes?: Record<string, string | null>
      isDefault?: boolean
    }) => {
      const { data, error } = await route().patch(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      put(data)
      // Making one the default makes another not.
      if (data) void queryClient.invalidateQueries({ queryKey: templateKeys.all() })
    },
  })

  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await route().delete()
      if (error) throw error
    },
    onSuccess: () => {
      const serviceTypeId = template.data?.serviceTypeId
      void queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' })
      if (serviceTypeId)
        void queryClient.invalidateQueries({ queryKey: templateKeys.list(serviceTypeId) })
    },
  })

  const addItem = useMutation({
    mutationKey: templateKeys.edit(templateId, 'addItem'),
    mutationFn: async (body: {
      kind: 'header' | 'media' | 'item'
      title?: string
      songId?: string | null
      lengthSeconds?: number | null
      servicePosition?: ServicePosition
    }) => {
      const { data, error } = await route().items.post(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })

  const updateItem = useMutation({
    mutationKey: templateKeys.edit(templateId, 'updateItem'),
    mutationFn: async ({
      itemId,
      ...body
    }: {
      itemId: string
      title?: string
      /** Link it to a song, or null to unlink. */
      songId?: string | null
      /** Who leads it on its plans; null clears it. */
      leaderPersonId?: string | null
      key?: string | null
      lengthSeconds?: number | null
      notes?: string | null
      arrangementId?: string
      servicePosition?: ServicePosition
      sequence?: string[] | null
      categoryNotes?: Record<string, string | null>
    }) => {
      const { data, error } = await route().items({ itemId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })

  const removeItem = useMutation({
    mutationKey: templateKeys.edit(templateId, 'removeItem'),
    mutationFn: async (itemId: string) => {
      const { data, error } = await route().items({ itemId }).delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })

  const reorder = useMutation({
    mutationKey: templateKeys.edit(templateId, 'reorder'),
    mutationFn: async (itemIds: string[]) => {
      const { data, error } = await route().order.put({ itemIds })
      if (error) throw error
      return data
    },
    onMutate: async (itemIds) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<PlanTemplate>(key)
      if (previous) {
        const byId = new Map(previous.items.map((item) => [item.id, item]))
        const ordered = byServicePosition(itemIds.flatMap((id) => byId.get(id) ?? []))
        const times = runningTimes(ordered)
        queryClient.setQueryData<PlanTemplate>(key, {
          ...previous,
          items: ordered.map((item, i) => ({
            ...item,
            startsAtSeconds: times[i]?.startsAt ?? 0,
            startsAtClock: clockAt(previous.startTime, times[i]?.startsAt ?? 0),
          })),
        })
      }
      return { previous }
    },
    onError: (_error, _ids, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSuccess: put,
  })

  /** Moves an item within its phase, as on a plan. */
  const move = (from: number, to: number) => {
    const items = template.data?.items ?? []
    const where = items[from]?.servicePosition
    const same = items.flatMap((item, i) => (item.servicePosition === where ? [i] : []))
    const first = same[0] ?? 0
    const last = same.at(-1) ?? items.length - 1
    reorder.mutate(
      reorderList(items, from, Math.min(last, Math.max(first, to))).map((item) => item.id),
    )
  }

  const addTime = useMutation({
    mutationKey: templateKeys.edit(templateId, 'addTime'),
    mutationFn: async (body: {
      kind: TemplateTimeKind
      name?: string | null
      dayOffset: number
      startTime: string
      endTime?: string | null
    }) => {
      const { data, error } = await route().times.post(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const updateTime = useMutation({
    mutationKey: templateKeys.edit(templateId, 'updateTime'),
    mutationFn: async ({
      timeId,
      ...body
    }: {
      timeId: string
      kind?: TemplateTimeKind
      name?: string | null
      dayOffset?: number
      startTime?: string
      endTime?: string | null
    }) => {
      const { data, error } = await route().times({ timeId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  /** Refused for the last service time. */
  const removeTime = useMutation({
    mutationKey: templateKeys.edit(templateId, 'removeTime'),
    mutationFn: async (timeId: string) => {
      const { data, error } = await route().times({ timeId }).delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })

  /** How many a position needs on its plans; null goes back to the position's usual. */
  const setNeed = useMutation({
    mutationKey: templateKeys.edit(templateId, 'setNeed'),
    mutationFn: async ({ positionId, needed }: { positionId: string; needed: number | null }) => {
      const { data, error } = await route().needs({ positionId }).put({ needed })
      if (error) throw error
      return data
    },
    onSuccess: put,
  })

  return {
    template: template.data,
    isLoading: template.isPending,
    error: template.error,
    updateTemplate,
    remove,
    addItem,
    updateItem,
    removeItem,
    reorder,
    move,
    addTime,
    updateTime,
    removeTime,
    setNeed,
  }
}

/** One step up (-1) or down (+1) in the church's order of a type's templates. */
export function useTemplateOrder(serviceTypeId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const reorder = useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await api.api.v1.services['service-types']({
        id: serviceTypeId,
      }).templates.order.put({ ids })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      if (data) queryClient.setQueryData(templateKeys.list(serviceTypeId), data)
    },
  })
  const move = (id: string, by: -1 | 1) => {
    const list = queryClient.getQueryData<TemplateSummary[]>(templateKeys.list(serviceTypeId)) ?? []
    const from = list.findIndex((t) => t.id === id)
    if (from < 0) return
    reorder.mutate(reorderList(list, from, from + by).map((t) => t.id))
  }
  return { reorder, move }
}

/**
 * The teams a template's plans use (its service type's), each position with how many it needs on them: the template's own
 * count, or the position's usual.
 */
export function useTemplateTeams(templateId: string) {
  const template = useTemplate(templateId).data
  const { data: teams } = useTeams()
  if (!template || !teams) return null
  const relevant = teams.filter((team) => team.serviceTypeIds.includes(template.serviceTypeId))
  return teamsForViewer(relevant, null, []).map(({ team }) => ({
    team,
    positions: team.positions.map((position) => {
      const override = template.needs.find((n) => n.positionId === position.id)?.needed
      return {
        ...position,
        needed: override ?? position.neededPerPlan,
        /** True when the template asks a different number than the position's usual. */
        overridden: override !== undefined,
      }
    }),
  }))
}

export type TemplateTeams = NonNullable<ReturnType<typeof useTemplateTeams>>

/** New plans of a service type: one or several, from a template or blank. */
export function useCreatePlans() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      serviceTypeId,
      ...body
    }: {
      serviceTypeId: string
      date: string
      templateId?: string | null
      title?: string | null
      repeat?: PlanRepeat | null
    }) => {
      const { data, error } = await api.api.v1.services['service-types']({
        id: serviceTypeId,
      }).plans.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
      // The service types list counts each type's upcoming plans.
      void queryClient.invalidateQueries({ queryKey: servicesKeys.types() })
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plan'] })
    },
  })
}

/** How often New plan repeats: once, weekly, or every N weeks. */
export type RepeatMode = 'once' | 'weekly' | 'every'

/** A choice in New plan's template picker: a template's id, or a blank plan. */
export const BLANK_TEMPLATE = 'blank'

const isoDay = (date: Date) => date.toISOString().slice(0, 10)

/**
 * New plan's form: which service, which day, which template (the service's
 * default first, then the others, then Blank), and whether it repeats —
 * weekly or every N weeks until a day — with how many plans that makes
 * ("Makes 6 plans: Nov 1 – Dec 6"), counted by the same function the
 * server uses. `submit` makes them; one plan is returned to open.
 */
export function useNewPlanForm({
  types,
  defaultTypeId,
}: {
  /** The service types they may plan for (Editor there). */
  types: readonly ServiceType[]
  /** The service whose plans are on screen: a new plan is one of them. */
  defaultTypeId: string
}) {
  const createPlans = useCreatePlans()
  const [picked, setTypeId] = useState('')
  const typeId =
    picked || (types.some((type) => type.id === defaultTypeId) ? defaultTypeId : types[0]?.id) || ''
  const type = types.find((t) => t.id === typeId)
  const [date, setDate] = useState(() => isoDay(new Date(Date.now() + 7 * 86_400_000)))
  // Null: the service's default (or Blank when it has none), until chosen.
  const [chosenTemplate, setTemplate] = useState<string | null>(null)
  const [mode, setMode] = useState<RepeatMode>('once')
  const [everyWeeks, setEveryWeeks] = useState(2)
  const [until, setUntil] = useState('')

  const list = useServiceTypeTemplates(typeId)
  const templates = templatePickerOrder(list.data ?? [])
  const fallback = templates.find((t) => t.isDefault)?.id ?? BLANK_TEMPLATE
  const templateId =
    chosenTemplate !== null &&
    (chosenTemplate === BLANK_TEMPLATE || templates.some((t) => t.id === chosenTemplate))
      ? chosenTemplate
      : fallback
  const template = templates.find((t) => t.id === templateId) ?? null

  // Until: a month and a bit on, unless chosen.
  const lastDay =
    until || isoDay(new Date(new Date(`${date}T12:00:00Z`).getTime() + 35 * 86_400_000))
  const repeat: PlanRepeat | null =
    mode === 'once' ? null : { everyWeeks: mode === 'weekly' ? 1 : everyWeeks, until: lastDay }
  const dates = date ? repeatDates(date, repeat) : []
  const problem = date ? repeatProblem(dates) : 'Choose a day.'

  const reset = () => {
    setTypeId('')
    setTemplate(null)
    setMode('once')
    setUntil('')
    createPlans.reset()
  }

  return {
    typeId,
    type,
    setTypeId: (id: string) => {
      setTypeId(id)
      // Another service's templates: its own default again.
      setTemplate(null)
    },
    date,
    setDate,
    templates,
    templatesLoading: list.isPending && typeId !== '',
    templateId,
    template,
    setTemplate,
    mode,
    setMode,
    everyWeeks,
    setEveryWeeks,
    until: lastDay,
    setUntil,
    dates,
    /** "Makes 6 plans: Nov 1 – Dec 6"; null when it makes just the one. */
    summary: mode === 'once' || problem ? null : repeatSummary(dates),
    problem,
    /** What the plan's first service starts at: the template's, or the service's usual. */
    startsAt: template
      ? (template.times.find((t) => t.kind === 'service')?.startTime ?? type?.defaultStartTime)
      : type?.defaultStartTime,
    pending: createPlans.isPending,
    error: createPlans.error,
    reset,
    submit: (onDone: (plans: { id: string; date: string }[]) => void) => {
      if (problem || !typeId) return
      createPlans.mutate(
        {
          serviceTypeId: typeId,
          date,
          templateId: templateId === BLANK_TEMPLATE ? null : templateId,
          repeat,
        },
        { onSuccess: (data) => onDone(data?.plans ?? []) },
      )
    },
  }
}
